import React from 'react';
import StatusBadge from '../StatusBadge';

interface MilestoneItem {
  id: string;
  title: string;
  dueAt: string;
  status: string;
  type?: string;
}

interface MilestoneListProps {
  milestones: MilestoneItem[];
  emptyIcon?: string;
  emptyMessage?: string;
  onViewAll?: () => void;
  viewAllLabel?: string;
}

const MILESTONE_TYPE_ICONS: Record<string, string> = {
  production: 'event',
  character_arc: 'person',
  task: 'flag',
};

/**
 * Compact milestone/deadline list used on dashboards and sidebars.
 * Lighter-weight than ProductionMilestones chart component — no card wrapper.
 *
 * @example
 * <MilestoneList
 *   milestones={upcomingDeadlines}
 *   emptyMessage="No upcoming deadlines"
 *   onViewAll={() => navigate('/home/timeline')}
 * />
 */
const MilestoneList: React.FC<MilestoneListProps> = ({
  milestones,
  emptyIcon = 'event',
  emptyMessage = 'No upcoming deadlines',
  onViewAll,
  viewAllLabel = 'View Timeline →',
}) => {
  if (milestones.length === 0) {
    return (
      <div className="text-center py-6">
        <span className="material-icons-outlined text-3xl text-on-surface-variant/15 block mb-2">
          {emptyIcon}
        </span>
        <p className="text-xs text-on-surface-variant/30">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-3">
        {milestones.map((m) => (
          <div
            key={m.id}
            className="flex items-center gap-3 p-3 bg-surface-container border border-outline-variant/10"
          >
            <div className="w-1.5 h-1.5 bg-tertiary" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-on-surface truncate">
                {m.title}
              </p>
              <p className="text-xs text-on-surface-variant/40">
                Due {new Date(m.dueAt).toLocaleDateString()}
              </p>
            </div>
            <span className="badge badge-primary text-[0.55rem]">
              {m.status}
            </span>
          </div>
        ))}
      </div>
      {onViewAll && (
        <div className="pt-3">
          <button
            onClick={onViewAll}
            className="btn btn-ghost btn-sm text-tertiary w-full"
          >
            {viewAllLabel}
          </button>
        </div>
      )}
    </>
  );
};

export default MilestoneList;